import * as React from "react";
import { Input, type InputProps } from "@/components/ui/input";
import { caretAfterDigits, formatUsDigits, typedUsDigits, usDigits } from "@/lib/phone-number";
import { cn } from "@/lib/utils";

/**
 * A phone field with its +1 fixed in front: every number the console takes is a US one (ACL-653),
 * so the rep types the ten digits after it and nothing else. The field draws them as `(310) 555 0142`
 * while they are typed, keeps the caret where the rep is editing, and gives back the digits alone
 * (`typedUsDigits`): a paste of `+1 (310) 555-0142` or `1-310-555-0142` loses its 1. `value` is
 * whatever the caller holds, a stored `+13105550142` or the digits typed so far: the field shows
 * what comes after the +1 either way (`usDigits`), so a number kept in another spelling reads
 * right and is not changed until the rep types.
 */
export function UsPhoneInput({
  value, onChange, className, disabled, ...props
}: Omit<InputProps, "value" | "onChange" | "type" | "inputMode" | "leading" | "placeholder"> & {
  value: string;
  onChange: (digits: string) => void;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  // How many digits sat before the caret when the rep last typed; placed again once the field is
  // redrawn, so a digit corrected in the middle leaves the caret in the middle.
  const caret = React.useRef<number | null>(null);
  const shown = formatUsDigits(usDigits(value));

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (caret.current === null || !el) return;
    if (document.activeElement === el) {
      const at = caretAfterDigits(shown, caret.current);
      el.setSelectionRange(at, at);
    }
    caret.current = null;
  }, [shown]);

  return (
    <div className="flex w-full">
      <span
        className={cn(
          "inline-flex h-8 shrink-0 select-none items-center gap-1.5 rounded-l-md border border-r-0",
          "border-border bg-surface-sunken px-2 text-sm",
          disabled ? "text-text-subtle" : "text-text-muted",
        )}
      >
        <span aria-hidden>🇺🇸</span>
        <span>+1</span>
      </span>
      <Input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="off"
        className={cn("rounded-l-none", className)}
        placeholder="(310) 555 0142"
        value={shown}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          caret.current = usDigits(raw.slice(0, e.target.selectionStart ?? raw.length)).length;
          onChange(typedUsDigits(raw));
        }}
        {...props}
      />
    </div>
  );
}
